import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const WorkspaceSidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: '/app-dashboard', 
      icon: 'LayoutDashboard',
      description: 'Project overview and metrics'
    },
    { 
      label: 'Best Practices', 
      path: '/best-practice-picker', 
      icon: 'CheckCircle2',
      description: 'SAP scope selection and configuration'
    },
    { 
      label: 'Questionnaire', 
      path: '/questionnaire-runner', 
      icon: 'FileQuestion',
      description: 'Requirements gathering with AI assistance'
    },
    { 
      label: 'Fit/Gap Analysis', 
      path: '/fit-gap-board', 
      icon: 'GitCompare',
      description: 'Kanban-based requirement assessment'
    },
    { 
      label: 'Documents', 
      path: '/document-generator', 
      icon: 'FileText',
      description: 'BPD²/FSD² generation and management'
    },
    { 
      label: 'Digital Signoffs', 
      path: '/digital-signoffs', 
      icon: 'PenTool',
      description: 'Approval workflows and audit trails'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-16 bottom-0 z-40 bg-card border-r border-border nav-transition ${
          isCollapsed ? 'w-16' : 'w-64'
        } ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            {!isCollapsed && (
              <h2 className="text-lg font-semibold text-foreground font-inter">
                Workspace
              </h2>
            )}
            <button
              onClick={onToggleCollapse}
              className="hidden md:flex p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted nav-transition"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon 
                name={isCollapsed ? "ChevronRight" : "ChevronLeft"} 
                size={20} 
              />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigationItems?.map((item) => {
              const isActive = isActivePath(item?.path);
              
              return (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-left nav-transition group ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-elevation-1'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  title={isCollapsed ? item?.label : item?.description}
                >
                  <Icon 
                    name={item?.icon} 
                    size={20} 
                    className={`flex-shrink-0 ${
                      isActive ? 'text-primary-foreground' : 'text-current'
                    }`}
                  />
                  {!isCollapsed && (
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {item?.label}
                      </div>
                      <div className={`text-xs truncate ${
                        isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      }`}>
                        {item?.description}
                      </div>
                    </div>
                  )}
                  {isActive && !isCollapsed && (
                    <div className="w-2 h-2 bg-primary-foreground rounded-full flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-border">
            <div className={`flex items-center space-x-3 ${isCollapsed ? 'justify-center' : ''}`}>
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                <Icon name="User" size={16} className="text-muted-foreground" />
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    SAP Architect
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    Enterprise Solutions
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>
      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border md:hidden">
        <div className="flex items-center justify-around py-2">
          {navigationItems?.slice(0, 5)?.map((item) => {
            const isActive = isActivePath(item?.path);
            
            return (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`flex flex-col items-center space-y-1 px-2 py-1 rounded-lg nav-transition ${
                  isActive
                    ? 'text-primary' :'text-muted-foreground'
                }`}
              >
                <Icon 
                  name={item?.icon} 
                  size={20} 
                  className="text-current"
                />
                <span className="text-xs font-medium">
                  {item?.label?.split(' ')?.[0]}
                </span>
              </button>
            );
          })}
          <button
            onClick={() => setIsMobileOpen(true)}
            className="flex flex-col items-center space-y-1 px-2 py-1 rounded-lg text-muted-foreground"
          >
            <Icon name="Menu" size={20} />
            <span className="text-xs font-medium">More</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default WorkspaceSidebar;